import { useLayoutEffect, useRef } from 'react';

/**
 * Keeps a ref holding the latest value, for code that outlives a render and
 * must not be set up again whenever the value changes: an event listener, a
 * timer or a subscription created in an effect, or a stable callback.
 *
 * Replaces writing `xRef.current = x` during render, which React's `refs` lint
 * rule forbids: the ref is updated in a layout effect after each render.
 * Because of that:
 * - read `ref.current` in effects and event handlers, never during render;
 * - during the render itself, and in any layout effect that runs before this
 *   one (a child's, or one declared earlier in the same component), it still
 *   holds the previous value. Every `useEffect` sees the new one.
 *
 * React 19.2's `useEffectEvent` is the official answer for a function called
 * only from effects; this hook covers values, and functions called from
 * anywhere, on React 18.
 *
 * @param {T} value - The value to keep current.
 * @return {{ readonly current: T }} The same ref on every render, whose
 *   `current` is the value from the latest committed render.
 */
export function useLatest<T>(value: T): { readonly current: T } {
  const ref = useRef(value);

  useLayoutEffect(() => {
    ref.current = value;
  });

  return ref;
}
